import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery, QueryResult, useMutation } from '@apollo/client'
import omit from 'lodash.omit'
import { Card, Page } from '@babylon/core-ui'
import {
  useTrackClick,
  TrackingElementType,
  useTracking,
  TrackingActionType,
} from '@babylon/tracking/react'
import { useProductConfig } from '@babylon/product-config'
import { UnexpectedError } from '../../Utils'
import CreateForm from './CreateForm'
import MemberQuery from '../MemberQuery'
import InviteSentConfirmation from '../../SetupAppointment/CreateInvite/InviteSentConfirmation'
import { AppointmentAnalyticsModuleName } from '../../analytics/types'
import GetServiceTypesQuery from './GetServiceTypesQuery'
import CreateAppointmentInvite from '../CreateInviteMutation'
import {
  getAllowedMediumsForSelectedMedium,
  filterByMediumServiceTypes,
  findServiceTypeById,
} from './FormWithServiceTypes.utils'
import { MediumTypes, Mediums } from './AppointmentMedium'
import ServiceType from '../../types/ServiceType'
import styles from '../DigitalAppointmentForm.module.css'
import { PreferredConsumerNetwork } from '../../hooks/usePreferredNetwork/usePreferredNetwork'
import { AdditionalAppointmentSlotType } from '../AdditionalAppointmentSlotTypeSelector'

interface Props {
  memberId: string
  faceToFaceInvitesEnabled?: boolean
  patientNetwork?: PreferredConsumerNetwork
}

const baseValues = {
  appointment_type: '',
  notes_for_member: '',
  duration_minutes: null,
  additional_appointment_slot_types: [],
}

type ServiceTypesData = {
  serviceTypes: ServiceType[]
}
type ServiceTypesVars = {
  memberUuid: string
  includingDigital: boolean
}

export default function FormWithServiceTypes({
  memberId,
  faceToFaceInvitesEnabled,
  patientNetwork,
}: Props) {
  const { getProp } = useProductConfig()
  const [isSelectMediumDisplayed, setIsSelectMediumDisplayed] = useState(
    !!faceToFaceInvitesEnabled
  )
  const { state } = useLocation()
  const [selectedMedium, setSelectedMedium] = useState<MediumTypes | null>(null)

  const { data: patientData } = useQuery(MemberQuery, {
    variables: { memberId },
  })

  const {
    loading: loadingServiceTypes,
    error: errorServiceTypes,
    data: dataServiceTypes,
  }: QueryResult = useQuery<ServiceTypesData, ServiceTypesVars>(
    GetServiceTypesQuery,
    {
      skip: !patientData?.patient?.uuid,
      fetchPolicy: 'network-only',
      variables: {
        includingDigital: true,
        memberUuid: patientData?.patient?.uuid,
      },
    }
  )

  useEffect(() => {
    if (!faceToFaceInvitesEnabled) {
      setIsSelectMediumDisplayed(false)
      setSelectedMedium(Mediums.digital)

      return
    }

    const hasServiceTypeWithF2F = dataServiceTypes?.serviceTypes.find(
      ({ allowed_mediums }) => allowed_mediums.includes('physical')
    )
    setIsSelectMediumDisplayed(!!hasServiceTypeWithF2F)

    if (state?.selectedMedium) {
      // set selected medium from navigation state if navigating back from availability screen
      setSelectedMedium(state.selectedMedium)
    } else {
      // set default medium which is only physical if there is a service type with physical medium
      setSelectedMedium(hasServiceTypeWithF2F ? Mediums.f2f : Mediums.digital)
    }
  }, [faceToFaceInvitesEnabled, dataServiceTypes, state?.selectedMedium])

  const [
    createInvite,
    { data: submittedData, loading: submitting, error: errorSubmit },
  ] = useMutation(CreateAppointmentInvite)

  const { trackClick } = useTrackClick({
    moduleName: AppointmentAnalyticsModuleName.digitalInvite,
  })

  const { trackEvent } = useTracking()

  const allowUrgentSlots = getProp(
    'clinicalCare.appointments.adminPortal',
    'allowInvitesToBookReservedSlots'
  )

  const onTrackClick = (elementName) => {
    trackClick({
      elementName,
      elementType: TrackingElementType.button,
    })
  }

  const handleSubmit = (values) => {
    if (!patientData?.patient?.uuid) {
      return
    }

    const includeMediumSelection = faceToFaceInvitesEnabled
      ? getAllowedMediumsForSelectedMedium(
          selectedMedium,
          findServiceTypeById(
            dataServiceTypes?.serviceTypes ?? [],
            values.appointment_type
          )?.allowed_mediums
        )
      : {}

    const createInviteForm = {
      service_type_uuid: values.appointment_type,
      notes_for_member: values.notes_for_member,
      duration_minutes: values.duration_minutes,
      member_uuid: patientData.patient.uuid,
      intimate: false,
      earliest_booking_date: new Date().toISOString(),
      ...includeMediumSelection,
      ...(allowUrgentSlots && {
        allow_reserved_slots: values.additional_appointment_slot_types.includes(
          AdditionalAppointmentSlotType.reservedSlot
        ),
        allow_urgent_slots: values.additional_appointment_slot_types.includes(
          AdditionalAppointmentSlotType.urgentSlot
        ),
      }),
    }
    createInvite({
      variables: {
        input: createInviteForm,
      },
    })

    onTrackClick('send-to-patient')
  }

  const error = errorServiceTypes || errorSubmit
  const loading = loadingServiceTypes || submitting
  const invite = submittedData?.createAppointmentInvite

  const isInviteSentConfirmationOpen = !submitting && !errorSubmit && !!invite

  if (isInviteSentConfirmationOpen) {
    trackEvent({
      moduleName: AppointmentAnalyticsModuleName.digitalInvite,
      actionType: TrackingActionType.popupLoaded,
      elementType: TrackingElementType.popup,
      elementName: 'invite-successfully-sent',
    })
  }

  const serviceTypes = filterByMediumServiceTypes(
    dataServiceTypes?.serviceTypes ?? [],
    selectedMedium
  )

  return (
    <Page
      title="Book an Appointment"
      breadcrumbs={[
        <Link key="1" to="/admin/patients/search">
          Patients
        </Link>,
        <Link key="2" to={`/admin/patients/${memberId}/memberships`}>
          Member details
        </Link>,
        'Book Appointment',
      ]}
    >
      {error && <UnexpectedError message={error.message} visible />}
      <InviteSentConfirmation
        className={styles.ModalButton}
        memberId={memberId}
        isOpen={isInviteSentConfirmationOpen}
        serviceTypeName={invite?.service_type?.name}
        durationMinutes={`${invite?.duration_minutes} minutes`}
        notes={invite?.notes_for_member}
        onTrackDoneBtn={() => onTrackClick('done')}
      />
      <Card>
        <CreateForm
          initialValues={{
            ...omit(
              baseValues,
              allowUrgentSlots ? [] : ['additional_appointment_slot_types']
            ),
            ...(state?.originalFilters || {}),
          }}
          loading={loading}
          appointmentTypes={serviceTypes}
          onSubmit={handleSubmit}
          memberId={memberId}
          selectedMedium={selectedMedium}
          setSelectedMedium={setSelectedMedium}
          isSelectMediumDisplayed={isSelectMediumDisplayed}
          serviceTypes={dataServiceTypes?.serviceTypes ?? []}
          patientNetwork={patientNetwork}
        />
      </Card>
    </Page>
  )
}
